import React, { useMemo } from 'react';
import SubmissionRow from './SubmissionRow';
import QualificationSubmissionDetails from './QualificationSubmissionDetails';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { Submission } from 'types/evaluation';
import { calculateAverageRank } from 'utils/submissionSorting';

interface FinalSubmissionsTableProps {
  submissions: Submission[];
  expandedRows: Set<string | number>;
  toggleRowExpansion: (id: string | number) => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  handleSort: (field: string) => void;
  getMainDescription: (submission: Submission) => string;
  rubrics?: any[];
}

const NO_RANK_SENTINEL = 999;

const FinalSubmissionsTable: React.FC<FinalSubmissionsTableProps> = ({
  submissions,
  expandedRows,
  toggleRowExpansion,
  sortField,
  sortDirection,
  handleSort,
  getMainDescription,
  rubrics,
}) => {
  const rankMap = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const submission of submissions) {
      const rank = calculateAverageRank(submission, submissions);
      map.set(submission.id, rank === NO_RANK_SENTINEL ? null : rank);
    }
    return map;
  }, [submissions]);

  const SortIndicator = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ArrowUpIcon className="w-3 h-3 text-white opacity-50" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUpIcon className="w-3 h-3 text-white" />
    ) : (
      <ArrowDownIcon className="w-3 h-3 text-white" />
    );
  };

  if (!submissions || submissions.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-baijamjuree font-bold text-gray-900 mb-2">
          No Final Submissions Found
        </h3>
        <p className="text-gray-600 font-baijamjuree">
          No final stage submissions are available for judgment review.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed min-w-[1200px] xl:min-w-full">
          <thead className="bg-gradient-to-r from-gray-800 to-gray-700">
            <tr>
              <th className="w-12 py-4 px-2"></th>
              <th
                className="text-left py-4 px-2 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '20%' }}
              >
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors"
                >
                  Project/Profile Name <SortIndicator field="title" />
                </button>
              </th>
              <th
                className="text-left py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '22%' }}
              >
                <button
                  onClick={() => handleSort('email')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors"
                >
                  Email <SortIndicator field="email" />
                </button>
              </th>
              <th
                className="text-left py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '18%' }}
              >
                <button
                  onClick={() => handleSort('pillar')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors"
                >
                  Pillar <SortIndicator field="pillar" />
                </button>
              </th>
              <th
                className="text-left py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '20%' }}
              >
                <button
                  onClick={() => handleSort('organization_unit')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors"
                >
                  Organization <SortIndicator field="organization_unit" />
                </button>
              </th>
              <th
                className="text-center py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '10%' }}
              >
                <button
                  onClick={() => handleSort('avg_rank')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors mx-auto"
                >
                  Avg Rank <SortIndicator field="avg_rank" />
                </button>
              </th>
              <th
                className="text-center py-4 px-2 font-baijamjuree font-semibold text-sm text-white"
                style={{ width: '10%' }}
              >
                <button
                  onClick={() => handleSort('total_score')}
                  className="flex items-center gap-1 hover:text-gray-200 transition-colors mx-auto"
                >
                  Score <SortIndicator field="total_score" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {submissions.map((submission, index) => (
              <React.Fragment key={submission.id}>
                <SubmissionRow
                  index={index}
                  submission={submission}
                  expanded={expandedRows.has(submission.id)}
                  toggleRowExpansion={toggleRowExpansion}
                  averageRank={rankMap.get(submission.id)}
                  showRanking={true}
                />
                {expandedRows.has(submission.id) && (
                  <QualificationSubmissionDetails
                    submission={submission}
                    getMainDescription={getMainDescription}
                    rubrics={rubrics}
                    allSubmissions={submissions}
                  />
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinalSubmissionsTable;
